<script setup lang="ts">
/**
 * PeekNote — a paper note with her peek_cat looking in from its bottom-right corner.
 * peek_cat is a cropped drawing (flat right + bottom edges), so it sits inside the
 * note and both cut edges run past the note's edges (clipped) — never in the air.
 * The same tuck as the /spread tiles. Content is left-aligned and runs the full width
 * of the note; only the bottom-right corner is kept clear for the cat (a floated,
 * diagonal `shape-outside` pinned to the bottom of the text column), so the text
 * wraps around its head instead of down a narrow column. Used for errors,
 * "not found", "are you sure?".
 */
import PaperCutout from './PaperCutout.vue';

withDefaults(defineProps<{ raised?: boolean; role?: string }>(), { raised: false, role: undefined });
</script>

<template>
  <div
    :class="['relative flex min-h-[152px] overflow-hidden px-5 py-6 text-left', raised ? 'paper-sheet-raised' : 'paper-sheet']"
    :role="role"
  >
    <!-- A stretched flex item has a definite height, so the ::before spacer (height 100%
         minus the cat's corner) can push the cat's float to the bottom of the column. -->
    <div class="peek-body min-w-0 flex-1">
      <span
        class="peek-corner"
        aria-hidden="true"
      />
      <slot />
    </div>
    <!-- The drawing's canvas is padded: its right/bottom cut edges sit at ~99% of it, so a
         small negative offset puts them just past the note's edges. -->
    <div
      class="pointer-events-none absolute -bottom-2 -right-2 w-[128px]"
      aria-hidden="true"
    >
      <PaperCutout
        name="peek_cat"
        still
      />
    </div>
  </div>
</template>

<style scoped>
/* The cat's corner, in the text column's coordinates: its head rises ~53px above the
   column's bottom and reaches ~58px in from its right edge, along a diagonal (ear tip
   top-right, chin bottom-left). The triangle is that diagonal pushed out by a ~10px gap. */
.peek-body {
  --peek-w: 72px;
  --peek-h: 64px;
}

.peek-body::before {
  content: '';
  float: right;
  width: 0;
  height: 100%;
  margin-bottom: calc(-1 * var(--peek-h));
}

.peek-corner {
  float: right;
  clear: right;
  width: var(--peek-w);
  height: var(--peek-h);
  shape-outside: polygon(100% 0, 100% 100%, 0 100%);
}

/* No orphaned last word on a title («…пошло не / так»); captions avoid a lone word too. */
.peek-body :slotted(:is(h1, h2, h3)) {
  text-wrap: balance;
}

.peek-body :slotted(p) {
  text-wrap: pretty;
}
</style>
